import { Link } from "@oysterhr/ui"
import { flowRight } from "lodash-es"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import type { CountryIsoCode } from "@oysterhr/schema"
import type { HiringTemplateCardFragment } from "../../../components/HiringTemplateCard"
import { HiringTemplateCard } from "../../../components/HiringTemplateCard"
import { getCompanyNewHireTemplateCardActions } from "../../../components/HiringTemplateCard/helpers"
import { makeLimitCards, makeFilterAndSortActiveCardsByRelevance } from "../helpers"
import { gql } from "@apollo/client"
import { countryCodeToName } from "@oysterhr/common"

export const ListHiringTemplates = ({
  countryCode,
  companyId,
  limit,
  onExpand: onExpand,
  employmentTemplates,
  title,
  description,
  subscriptionPlanId,
  employmentId,
}: {
  countryCode: CountryIsoCode
  companyId?: string
  limit?: number
  onExpand?: () => void
  employmentTemplates: ListHiringTemplatesFragment[]
  title: string
  description?: string
  subscriptionPlanId?: string
  employmentId?: string
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation("hiring_templates/Common")

  const isOysterTemplatesSection = employmentTemplates[0]?.oysterScore !== null
  const countryName = countryCodeToName(countryCode)

  const templates = useMemo(() => {
    return flowRight(
      makeLimitCards(limit),
      makeFilterAndSortActiveCardsByRelevance(countryCode),
    )(employmentTemplates)
  }, [employmentTemplates, countryCode, limit])

  return (
    <div>
      <div className="mb-6 text-2xl font-semibold text-primary-base">
        {title}
        {isOysterTemplatesSection && ` for ${countryName}`}
      </div>
      <div className="mb-6 text-primary-base">{description}</div>
      <div className="space-y-4">
        <div className="grid flex-1 grid-cols-1 gap-3 md:max-w-screen-xl md:grid-cols-2 lg:grid-cols-2 lg:gap-6 xl:grid-cols-3">
          {templates?.map(template => (
            <HiringTemplateCard
              key={template.id || template.oysterScore}
              employmentTemplate={template}
              actions={getCompanyNewHireTemplateCardActions({
                companyId,
                hiringTemplate: template,
                navigate,
                countryCode,
                subscriptionPlanId,
                employmentId,
                t,
              })}
            />
          ))}
        </div>
        {onExpand && limit && employmentTemplates?.length > limit && (
          <div className=" flex w-full justify-center text-center md:max-w-screen-xl">
            <Link
              to=""
              onClick={e => {
                e.preventDefault()
                onExpand?.()
              }}
            >
              {t("listTemplates.seeAll")}
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

ListHiringTemplates.fragment = gql`
  fragment ListHiringTemplates on EmploymentTemplate {
    ...HiringTemplateCard
  }
  ${HiringTemplateCard.fragment}
`

// @todo/codegen: Remove this type when we can use the generated type from the fragment
export type ListHiringTemplatesFragment = HiringTemplateCardFragment
